import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { Activity, Users, CreditCard, Database, TrendingUp, TrendingDown, FileText } from "lucide-react";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { useRef } from "react";
import TenantDetails from "@/pages/TenantDetails";
import { addDays, format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths, startOfYear, endOfYear, subYears, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTenant } from "@/contexts/TenantContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTenantsHealthSummary } from "@/hooks/useTenantHealth";
import { useSearchParams } from "react-router-dom";

const SaasOverview = () => {
  const { isAdminSaas } = useTenant();
  const defaultFrom = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const defaultTo = useMemo(() => endOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const [cadFrom, setCadFrom] = useState<Date>(defaultFrom);
  const [cadTo, setCadTo] = useState<Date>(defaultTo);
  const [activeTab, setActiveTab] = useState<string>("financeira");
  const [tenantProfile, setTenantProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get("tab");
    const fromStr = searchParams.get("from");
    const toStr = searchParams.get("to");
    if (tab) setActiveTab(tab);
    if (fromStr) {
      const d = new Date(fromStr);
      if (!isNaN(d.getTime())) setCadFrom(startOfDay(d));
    }
    if (toStr) {
      const d = new Date(toStr);
      if (!isNaN(d.getTime())) setCadTo(endOfDay(d));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", activeTab);
    next.set("from", format(cadFrom, "yyyy-MM-dd"));
    next.set("to", format(cadTo, "yyyy-MM-dd"));
    setSearchParams(next);
  }, [activeTab, cadFrom, cadTo]);

  const prevFrom = useMemo(() => {
    const days = Math.max(1, differenceInCalendarDays(cadTo, cadFrom) + 1);
    return startOfDay(subDays(cadFrom, days));
  }, [cadFrom, cadTo]);
  const prevTo = useMemo(() => {
    return endOfDay(subDays(cadFrom, 1));
  }, [cadFrom]);


  const { data: tenants = [], isLoading: loadingTenants } = useQuery({
    queryKey: ["saas-tenants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select(`id, name, email, created_at, subscription_status, subscription_plan_id, subscription_type, subscription_payment_method, subscription_start_date, subscription_next_billing_date, subscription_last_payment_date`) as any;
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: subscriptionTxs = [] } = useQuery({
    queryKey: ["saas-subscription-transactions", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("id, tenant_id, amount, status, payment_type, expiration_date, payment_date")
        .gte("payment_date", cadFrom.toISOString())
        .lte("payment_date", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: subscriptionTxsPrev = [] } = useQuery({
    queryKey: ["saas-subscription-transactions-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("id, tenant_id, amount, status, payment_type, expiration_date, payment_date")
        .gte("payment_date", prevFrom.toISOString())
        .lte("payment_date", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ["saas-transactions", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select(`id, created_at, tipo, status, valor_total, valor_liquido, tenant_id, payment_method_id, bank_account_id`)
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: transactionsPrev = [] } = useQuery({
    queryKey: ["saas-transactions-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select(`id, created_at, tipo, status, valor_total, valor_liquido, tenant_id, payment_method_id, bank_account_id`)
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: confirmedPays = [] } = useQuery({
    queryKey: ["saas-transaction-pays", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transaction_payments")
        .select(`id, valor, status, bank_account_id, payment_method_id, created_at, confirmed_at, transactions!inner(id, created_at, tenant_id, tipo)`) 
        .gte("transactions.created_at", cadFrom.toISOString())
        .lte("transactions.created_at", cadTo.toISOString())
        .eq("status", "confirmado");
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: confirmedPaysPrev = [] } = useQuery({
    queryKey: ["saas-transaction-pays-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transaction_payments")
        .select(`id, valor, status, bank_account_id, payment_method_id, created_at, confirmed_at, transactions!inner(id, created_at, tenant_id, tipo)`)
        .gte("transactions.created_at", prevFrom.toISOString())
        .lte("transactions.created_at", prevTo.toISOString())
        .eq("status", "confirmado");
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["saas-payment-methods"],
    queryFn: async () => {
      const { data, error } = await supabase.from("payment_methods").select("id, nome");
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const pmName = useMemo(() => {
    const m = new Map<string, string>();
    (paymentMethods || []).forEach((p: any) => m.set(String(p.id), p.nome));
    return m;
  }, [paymentMethods]);

  const { data: aiUsage = [] } = useQuery({
    queryKey: ["saas-ai-usage", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_ai_usage")
        .select("tenant_id, total_credits, used_credits, updated_at");
      if (error) return [];
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const kpis = useMemo(() => {
    const subsApproved = subscriptionTxs.filter((t: any) => t.status === "approved" && t.payment_type === "regular");
    const subsRevenue = subsApproved.reduce((sum: number, t: any) => sum + Number(t.amount || 0), 0);
    const entradas = transactions.filter((t: any) => t.tipo === "entrada");
    const saidas = transactions.filter((t: any) => t.tipo === "saida");
    const vendasTotais = entradas.length;
    const despesasTotais = saidas.length;

    const subsApprovedPrev = subscriptionTxsPrev.filter((t: any) => t.status === "approved" && t.payment_type === "regular");
    const subsRevenuePrev = subsApprovedPrev.reduce((sum: number, t: any) => sum + Number(t.amount || 0), 0);
    const entradasPrev = transactionsPrev.filter((t: any) => t.tipo === "entrada");
    const saidasPrev = transactionsPrev.filter((t: any) => t.tipo === "saida");
    const vendasTotaisPrev = entradasPrev.length;
    const despesasTotaisPrev = saidasPrev.length;

    const tenantsCreated = tenants.filter((t: any) => {
      const d = new Date(t.created_at);
      return d >= cadFrom && d <= cadTo;
    }).length;
    const tenantsCreatedPrev = tenants.filter((t: any) => {
      const d = new Date(t.created_at);
      return d >= prevFrom && d <= prevTo;
    }).length;

    const txCount = transactions.length;
    const tenantsCount = tenants.length;
    const subsCount = subsApproved.length;
    const netSaas = subsRevenue;
    return { subsRevenue, vendasTotais, despesasTotais, subsRevenuePrev, vendasTotaisPrev, despesasTotaisPrev, tenantsCreated, tenantsCreatedPrev, netSaas, txCount, tenantsCount, subsCount };
  }, [subscriptionTxs, transactions, subscriptionTxsPrev, transactionsPrev, tenants, cadFrom, cadTo, prevFrom, prevTo]);

  const pct = (curr: number, prev: number) => {
    if (!prev) return curr > 0 ? 100 : 0;
    return ((curr - prev) / prev) * 100;
  };

  const daysKeys = useMemo(() => {
    const d: string[] = [];
    for (let dt = new Date(cadFrom); dt <= cadTo; dt = addDays(dt, 1)) d.push(format(dt, "dd/MM", { locale: ptBR }));
    return d;
  }, [cadFrom, cadTo]);

  const temporalSeries = useMemo(() => {
    const base = new Map<string, { receita: number; subs: number; saidas: number; entradas_qtd: number; saidas_qtd: number }>();
    daysKeys.forEach((k) => base.set(k, { receita: 0, subs: 0, saidas: 0, entradas_qtd: 0, saidas_qtd: 0 }));
    transactions.forEach((t: any) => {
      const k = format(new Date(t.created_at), "dd/MM", { locale: ptBR });
      const prev = base.get(k);
      if (!prev) return;
      if (t.tipo === "entrada") {
        prev.receita += Number(t.valor_liquido || 0);
        prev.entradas_qtd += 1;
      }
      if (t.tipo === "saida") {
        prev.saidas += Number(t.valor_liquido || 0);
        prev.saidas_qtd += 1;
      }
    });
    subscriptionTxs.forEach((s: any) => {
      if (!s.payment_date) return;
      const k = format(new Date(s.payment_date), "dd/MM", { locale: ptBR });
      const prev = base.get(k);
      if (!prev) return;
      if (s.status === "approved") prev.subs += Number(s.amount || 0);
    });
    return Array.from(base.entries()).map(([dia, v]) => ({ dia, ...v }));
  }, [transactions, subscriptionTxs, daysKeys]);

  const methodRevenue = useMemo(() => {
    const map = new Map<string, number>();
    confirmedPays.forEach((p: any) => {
      const val = Number(p?.valor || 0);
      const idKey = String(p?.payment_method_id || "-");
      const nameKey = pmName.get(idKey) || idKey;
      map.set(nameKey, (map.get(nameKey) || 0) + val);
    });
    return Array.from(map.entries()).map(([metodo, valor]) => ({ metodo, valor }));
  }, [confirmedPays, pmName]);

  const { data: allProducts = [] } = useQuery({
    queryKey: ["saas-all-products", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allClients = [] } = useQuery({
    queryKey: ["saas-all-clients", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allSuppliers = [] } = useQuery({
    queryKey: ["saas-all-suppliers", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allProdCats = [] } = useQuery({
    queryKey: ["saas-all-product-cats", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_categories")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allExpCats = [] } = useQuery({
    queryKey: ["saas-all-expense-cats", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: allProductsPrev = [] } = useQuery({
    queryKey: ["saas-all-products-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allClientsPrev = [] } = useQuery({
    queryKey: ["saas-all-clients-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allSuppliersPrev = [] } = useQuery({
    queryKey: ["saas-all-suppliers-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allProdCatsPrev = [] } = useQuery({
    queryKey: ["saas-all-product-cats-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_categories")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allExpCatsPrev = [] } = useQuery({
    queryKey: ["saas-all-expense-cats-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_categories")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: allProfiles = [] } = useQuery({
    queryKey: ["saas-all-profiles", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allProfilesPrev = [] } = useQuery({
    queryKey: ["saas-all-profiles-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: allBankAccounts = [] } = useQuery({
    queryKey: ["saas-all-bank-accounts", cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, tenant_id, created_at")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });
  const { data: allBankAccountsPrev = [] } = useQuery({
    queryKey: ["saas-all-bank-accounts-prev", prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, tenant_id, created_at")
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const topTenantsByUsage = useMemo(() => {
    const countBy = (rows: any[]) => {
      const m = new Map<string, number>();
      rows.forEach((r: any) => m.set(r.tenant_id, (m.get(r.tenant_id) || 0) + 1));
      return m;
    };
    const prodM = countBy(allProducts);
    const cliM = countBy(allClients);
    const supM = countBy(allSuppliers);
    const pcM = countBy(allProdCats);
    const ecM = countBy(allExpCats);
    const prodPrevM = countBy(allProductsPrev);
    const cliPrevM = countBy(allClientsPrev);
    const supPrevM = countBy(allSuppliersPrev);
    const pcPrevM = countBy(allProdCatsPrev);
    const ecPrevM = countBy(allExpCatsPrev);
    const vendasM = new Map<string, number>();
    const despesasM = new Map<string, number>();
    transactions.forEach((t: any) => {
      if (t.tipo === "entrada") vendasM.set(t.tenant_id, (vendasM.get(t.tenant_id) || 0) + 1);
      if (t.tipo === "saida") despesasM.set(t.tenant_id, (despesasM.get(t.tenant_id) || 0) + 1);
    });
    const vendasPrevM = new Map<string, number>();
    const despesasPrevM = new Map<string, number>();
    transactionsPrev.forEach((t: any) => {
      if (t.tipo === "entrada") vendasPrevM.set(t.tenant_id, (vendasPrevM.get(t.tenant_id) || 0) + 1);
      if (t.tipo === "saida") despesasPrevM.set(t.tenant_id, (despesasPrevM.get(t.tenant_id) || 0) + 1);
    });
    const mergedIds = new Set<string>([...tenants.map((t: any) => t.id), ...Array.from(prodM.keys()), ...Array.from(cliM.keys()), ...Array.from(supM.keys()), ...Array.from(pcM.keys()), ...Array.from(ecM.keys()), ...Array.from(vendasM.keys()), ...Array.from(despesasM.keys())]);
    const rows = Array.from(mergedIds).map((tid) => {
      const tenant = tenants.find((t: any) => t.id === tid);
      const txsTenant = transactions.filter((t: any) => t.tenant_id === tid);
      const lastTx = txsTenant.length > 0 ? txsTenant.reduce((max: Date | null, t: any) => {
        const d = new Date(t.created_at);
        return !max || d > max ? d : max;
      }, null as Date | null) : null;
      const subsTenant = subscriptionTxs.filter((s: any) => s.tenant_id === tid && s.payment_date);
      const lastSub = subsTenant.length > 0 ? subsTenant.reduce((max: Date | null, s: any) => {
        const d = new Date(s.payment_date);
        return !max || d > max ? d : max;
      }, null as Date | null) : null;
      const ultimoAcesso = [lastTx, lastSub].filter(Boolean).sort((a: any, b: any) => (b as Date).getTime() - (a as Date).getTime())[0] || null;
      return {
        tenant_id: tid,
        name: tenant?.name || tid,
        ultimo_acesso: ultimoAcesso,
        produtos: prodM.get(tid) || 0,
        produtos_prev: prodPrevM.get(tid) || 0,
        clientes: cliM.get(tid) || 0,
        clientes_prev: cliPrevM.get(tid) || 0,
        fornecedores: supM.get(tid) || 0,
        fornecedores_prev: supPrevM.get(tid) || 0,
        categorias_produto: pcM.get(tid) || 0,
        categorias_produto_prev: pcPrevM.get(tid) || 0,
        categorias_despesa: ecM.get(tid) || 0,
        categorias_despesa_prev: ecPrevM.get(tid) || 0,
        vendas: vendasM.get(tid) || 0,
        vendas_prev: vendasPrevM.get(tid) || 0,
        despesas: despesasM.get(tid) || 0,
        despesas_prev: despesasPrevM.get(tid) || 0,
      };
    });
    const byName = new Map<string, any>();
    rows.forEach((r: any) => {
      const key = String(r.name || r.tenant_id).trim().toLowerCase();
      const prev = byName.get(key);
      if (!prev) {
        byName.set(key, { ...r });
      } else {
        const ultimoAcesso = [prev.ultimo_acesso, r.ultimo_acesso].filter(Boolean).sort((a: any, b: any) => (b as Date).getTime() - (a as Date).getTime())[0] || null;
        byName.set(key, {
          tenant_id: prev.tenant_id,
          name: prev.name,
          ultimo_acesso: ultimoAcesso,
          produtos: (prev.produtos || 0) + (r.produtos || 0),
          produtos_prev: (prev.produtos_prev || 0) + (r.produtos_prev || 0),
          clientes: (prev.clientes || 0) + (r.clientes || 0),
          clientes_prev: (prev.clientes_prev || 0) + (r.clientes_prev || 0),
          fornecedores: (prev.fornecedores || 0) + (r.fornecedores || 0),
          fornecedores_prev: (prev.fornecedores_prev || 0) + (r.fornecedores_prev || 0),
          categorias_produto: (prev.categorias_produto || 0) + (r.categorias_produto || 0),
          categorias_produto_prev: (prev.categorias_produto_prev || 0) + (r.categorias_produto_prev || 0),
          categorias_despesa: (prev.categorias_despesa || 0) + (r.categorias_despesa || 0),
          categorias_despesa_prev: (prev.categorias_despesa_prev || 0) + (r.categorias_despesa_prev || 0),
          vendas: (prev.vendas || 0) + (r.vendas || 0),
          vendas_prev: (prev.vendas_prev || 0) + (r.vendas_prev || 0),
          despesas: (prev.despesas || 0) + (r.despesas || 0),
          despesas_prev: (prev.despesas_prev || 0) + (r.despesas_prev || 0),
        });
      }
    });
    const onlyTenants = Array.from(byName.values()).filter((r: any) => {
      const k = String(r.name || r.tenant_id).trim().toLowerCase();
      return (tenants || []).some((t: any) => String(t.name || t.id).trim().toLowerCase() === k);
    });
    return onlyTenants.sort((a, b) => (b.vendas + b.despesas) - (a.vendas + a.despesas)).slice(0, 10);
  }, [allProducts, allClients, allSuppliers, allProdCats, allExpCats, allProductsPrev, allClientsPrev, allSuppliersPrev, allProdCatsPrev, allExpCatsPrev, transactions, transactionsPrev, tenants]);

  const inactiveTenants = useMemo(() => {
    const lastActivityById = new Map<string, Date | null>();
    tenants.forEach((t: any) => lastActivityById.set(t.id, null));
    transactions.forEach((t: any) => {
      const d = new Date(t.created_at);
      const prev = lastActivityById.get(t.tenant_id);
      if (!prev || d > prev) lastActivityById.set(t.tenant_id, d);
    });
    const byName = new Map<string, { id: string; name: string; last: Date | null }>();
    tenants.forEach((t: any) => {
      const key = String(t.name || t.id).trim().toLowerCase();
      const last = lastActivityById.get(t.id) || null;
      const prev = byName.get(key);
      if (!prev) {
        byName.set(key, { id: t.id, name: t.name, last });
      } else {
        const nextLast = [prev.last, last].filter(Boolean).sort((a: any, b: any) => (b as Date).getTime() - (a as Date).getTime())[0] || null;
        byName.set(key, { id: prev.id, name: prev.name, last: nextLast });
      }
    });
    const threshold = subDays(new Date(), 14);
    const list = Array.from(byName.values()).filter((x) => !x.last || (x.last as Date) < threshold);
    return list.sort((a, b) => {
      const ta = a.last ? a.last.getTime() : 0;
      const tb = b.last ? b.last.getTime() : 0;
      return ta - tb;
    }).slice(0, 10);
  }, [transactions, tenants]);

  const computeCadMetricRows = useMemo(() => {
    const makeMap = (rows: any[]) => {
      const m = new Map<string, number>();
      (rows || []).forEach((r: any) => {
        const tid = r?.tenant_id;
        if (!tid) return;
        m.set(tid, (m.get(tid) || 0) + 1);
      });
      return m;
    };
    const maps: Record<string, { curr: Map<string, number>; prev: Map<string, number> }> = {
      profiles: { curr: makeMap(allProfiles), prev: makeMap(allProfilesPrev) },
      products: { curr: makeMap(allProducts), prev: makeMap(allProductsPrev) },
      clients: { curr: makeMap(allClients), prev: makeMap(allClientsPrev) },
      suppliers: { curr: makeMap(allSuppliers), prev: makeMap(allSuppliersPrev) },
      bank_accounts: { curr: makeMap(allBankAccounts), prev: makeMap(allBankAccountsPrev) },
      product_categories: { curr: makeMap(allProdCats), prev: makeMap(allProdCatsPrev) },
      expense_categories: { curr: makeMap(allExpCats), prev: makeMap(allExpCatsPrev) },
      transacoes: { curr: makeMap(transactions), prev: makeMap(transactionsPrev) },
      vendas: { curr: makeMap((transactions || []).filter((t: any) => t.tipo === 'entrada')), prev: makeMap((transactionsPrev || []).filter((t: any) => t.tipo === 'entrada')) },
      despesas: { curr: makeMap((transactions || []).filter((t: any) => t.tipo === 'saida')), prev: makeMap((transactionsPrev || []).filter((t: any) => t.tipo === 'saida')) },
      pagamentos: {
        curr: makeMap((confirmedPays || []).map((p: any) => ({ tenant_id: p?.transactions?.tenant_id }))),
        prev: makeMap((confirmedPaysPrev || []).map((p: any) => ({ tenant_id: p?.transactions?.tenant_id }))),
      },
      tenants: {
        curr: makeMap((tenants || []).filter((t: any) => { const d = new Date(t.created_at); return d >= cadFrom && d <= cadTo; }).map((t: any) => ({ tenant_id: t.id }))),
        prev: makeMap((tenants || []).filter((t: any) => { const d = new Date(t.created_at); return d >= prevFrom && d <= prevTo; }).map((t: any) => ({ tenant_id: t.id }))),
      },
    };
    return computeCadMetricRowsFactory({ tenants: tenants || [], maps });
  }, [tenants, allProfiles, allProfilesPrev, allProducts, allProductsPrev, allClients, allClientsPrev, allSuppliers, allSuppliersPrev, allBankAccounts, allBankAccountsPrev, allProdCats, allProdCatsPrev, allExpCats, allExpCatsPrev, transactions, transactionsPrev, confirmedPays, confirmedPaysPrev, cadFrom, cadTo, prevFrom, prevTo]);

  const healthSummary = useTenantsHealthSummary({ tenantIds: (tenants || []).map((t: any) => t.id), from: cadFrom, to: cadTo });
  const churnRisk = useMemo(() => {
    const itemsById = (healthSummary.data || []).map((m: any) => ({ id: m.tenant_id, name: (tenants || []).find((t: any) => t.id === m.tenant_id)?.name || m.tenant_id, score: m.churn_score }));
    const byName = new Map<string, any>();
    itemsById.forEach((r: any) => {
      const key = String(r.name || r.id).trim().toLowerCase();
      const prev = byName.get(key);
      if (!prev) {
        byName.set(key, { ...r });
      } else {
        byName.set(key, { id: prev.id, name: prev.name, score: Math.max(prev.score || 0, r.score || 0) });
      }
    });
    return Array.from(byName.values()).sort((a: any, b: any) => b.score - a.score).slice(0, 10);
  }, [healthSummary.data, tenants]);

  const [drillDown, setDrillDown] = useState<{ title: string; rows: any[] } | null>(null);
  const [cadMetric, setCadMetric] = useState<{ key: string; label: string } | null>(null);

  if (!isAdminSaas) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Acesso restrito</h1>
        <p className="text-muted-foreground">Somente Admin SaaS pode visualizar esta página.</p>
      </div>
    );
  }

  const COLORS = ["#16a34a", "#2563eb", "#f59e0b", "#ef4444", "#0ea5e9", "#8b5cf6", "#14b8a6", "#f43f5e", "#22c55e", "#a855f7"]; 
  const formatCurrency = (value: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2"><Activity className="h-7 w-7" /> Visão Geral SaaSSS Editado</h1>
          <p className="text-sm md:text-base text-muted-foreground">Saúde, evolução e uso do sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Período</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(now)); setCadTo(endOfDay(now)); }}>Hoje</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const y = subDays(new Date(), 1); setCadFrom(startOfDay(y)); setCadTo(endOfDay(y)); }}>Ontem</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const d = subDays(new Date(), 2); setCadFrom(startOfDay(d)); setCadTo(endOfDay(d)); }}>Antes de ontem</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfWeek(now, { weekStartsOn: 1 })); setCadTo(endOfWeek(now, { weekStartsOn: 1 })); }}>Esta semana</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subDays(new Date(), 7); setCadFrom(startOfWeek(ref, { weekStartsOn: 1 })); setCadTo(endOfWeek(ref, { weekStartsOn: 1 })); }}>Semana passada</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subDays(new Date(), 14); setCadFrom(startOfWeek(ref, { weekStartsOn: 1 })); setCadTo(endOfWeek(ref, { weekStartsOn: 1 })); }}>Semana retrasada</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfMonth(now)); setCadTo(endOfMonth(now)); }}>Este mês</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subMonths(new Date(), 1); setCadFrom(startOfMonth(ref)); setCadTo(endOfMonth(ref)); }}>Mês passado</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subMonths(new Date(), 2); setCadFrom(startOfMonth(ref)); setCadTo(endOfMonth(ref)); }}>Mês retrasado</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfYear(now)); setCadTo(endOfYear(now)); }}>Este ano</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subYears(new Date(), 1); setCadFrom(startOfYear(ref)); setCadTo(endOfYear(ref)); }}>Ano passado</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const ref = subYears(new Date(), 2); setCadFrom(startOfYear(ref)); setCadTo(endOfYear(ref)); }}>Ano retrasado</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 2))); setCadTo(endOfDay(now)); }}>Últimos 3 dias</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 6))); setCadTo(endOfDay(now)); }}>Últimos 7 dias</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 29))); setCadTo(endOfDay(now)); }}>Últimos 30 dias</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 59))); setCadTo(endOfDay(now)); }}>Últimos 60 dias</DropdownMenuItem>
              <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 89))); setCadTo(endOfDay(now)); }}>Últimos 90 dias</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-2">
            <Input type="date" value={format(cadFrom, 'yyyy-MM-dd')} onChange={(e) => setCadFrom(startOfDay(new Date(e.target.value)))} />
            <span className="text-muted-foreground">até</span>
            <Input type="date" value={format(cadTo, 'yyyy-MM-dd')} onChange={(e) => setCadTo(endOfDay(new Date(e.target.value)))} />
          </div>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm">Receita de Assinaturas</CardTitle>
            <CreditCard className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-primary">{formatCurrency(kpis.subsRevenue)}</div>
            <p className="text-xs text-muted-foreground">No período</p>
            <div className="mt-1 flex items-center gap-1 text-xs">
              {(pct(kpis.subsRevenue, kpis.subsRevenuePrev) >= 0) ? (
                <TrendingUp className="h-4 w-4 text-success" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className={(pct(kpis.subsRevenue, kpis.subsRevenuePrev) >= 0) ? "text-success" : "text-destructive"}>
                {Math.abs(pct(kpis.subsRevenue, kpis.subsRevenuePrev)).toFixed(1)}%
              </span>
              <span className="text-muted-foreground">vs período anterior</span>
              <span className="text-muted-foreground">({formatCurrency(kpis.subsRevenuePrev)})</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm">Vendas Totais</CardTitle>
            <Database className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-success">{kpis.vendasTotais}</div>
            <p className="text-xs text-muted-foreground">Entre todos os clientes saas</p>
            <div className="mt-1 flex items-center gap-1 text-xs">
              {(pct(kpis.vendasTotais, kpis.vendasTotaisPrev) >= 0) ? (
                <TrendingUp className="h-4 w-4 text-success" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className={(pct(kpis.vendasTotais, kpis.vendasTotaisPrev) >= 0) ? "text-success" : "text-destructive"}>
                {Math.abs(pct(kpis.vendasTotais, kpis.vendasTotaisPrev)).toFixed(1)}%
              </span>
              <span className="text-muted-foreground">vs período anterior</span>
              <span className="text-muted-foreground">({kpis.vendasTotaisPrev})</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm">Despesas Totais</CardTitle>
            <Activity className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold text-destructive">{kpis.despesasTotais}</div>
            <p className="text-xs text-muted-foreground">Entre todos os clientes saas</p>
            <div className="mt-1 flex items-center gap-1 text-xs">
              {(pct(kpis.despesasTotais, kpis.despesasTotaisPrev) >= 0) ? (
                <TrendingUp className="h-4 w-4 text-success" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className={(pct(kpis.despesasTotais, kpis.despesasTotaisPrev) >= 0) ? "text-success" : "text-destructive"}>
                {Math.abs(pct(kpis.despesasTotais, kpis.despesasTotaisPrev)).toFixed(1)}%
              </span>
              <span className="text-muted-foreground">vs período anterior</span>
              <span className="text-muted-foreground">({kpis.despesasTotaisPrev})</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs md:text-sm">Clientes SaaS</CardTitle>
            <Users className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-xl md:text-2xl font-bold">{kpis.tenantsCreated}</div>
            <p className="text-xs text-muted-foreground">Cadastrados no período</p>
            <div className="mt-1 flex items-center gap-1 text-xs">
              {(pct(kpis.tenantsCreated, kpis.tenantsCreatedPrev) >= 0) ? (
                <TrendingUp className="h-4 w-4 text-success" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className={(pct(kpis.tenantsCreated, kpis.tenantsCreatedPrev) >= 0) ? "text-success" : "text-destructive"}>
                {Math.abs(pct(kpis.tenantsCreated, kpis.tenantsCreatedPrev)).toFixed(1)}%
              </span>
              <span className="text-muted-foreground">vs período anterior</span>
              <span className="text-muted-foreground">({kpis.tenantsCreatedPrev})</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v)} className="w-full">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="financeira">Financeira</TabsTrigger>
          <TabsTrigger value="cadastros">Cadastros</TabsTrigger>
          <TabsTrigger value="frequencia">Frequência</TabsTrigger>
          <TabsTrigger value="assinaturas">Assinaturas</TabsTrigger>
          <TabsTrigger value="transacoes">Transações</TabsTrigger>
          <TabsTrigger value="ia">IA</TabsTrigger>
          <TabsTrigger value="engajamento">Engajamento</TabsTrigger>
          <TabsTrigger value="churn">Churn e Risco</TabsTrigger>
        </TabsList>

        <TabsContent value="financeira" className="space-y-4 mt-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Assinaturas (por dia)</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={temporalSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" />
                    <YAxis tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} />
                    <Tooltip formatter={(v: any) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} />
                    <Legend />
                    <Line type="monotone" dataKey="subs" stroke="#2563eb" name="Assinaturas" onClick={(d: any) => setDrillDown({ title: `Assinaturas do dia ${d?.dia || ""}`, rows: subscriptionTxs.filter((s: any) => format(new Date(s.payment_date), "dd/MM", { locale: ptBR }) === d?.dia) })} style={{ cursor: "pointer" }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Receita por Método</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip />
                    <Legend />
                    <Pie data={methodRevenue} dataKey="valor" nameKey="metodo" outerRadius={80} onClick={(entry: any) => {
                      const methodName = entry?.metodo;
                      const txIds = confirmedPays.filter((p: any) => {
                        const idKey = String(p?.payment_method_id || "-");
                        const nameKey = pmName.get(idKey) || idKey;
                        return nameKey === methodName;
                      }).map((p: any) => p.transactions?.id).filter(Boolean);
                      const rows = transactions.filter((t: any) => txIds.includes(t.id));
                      setDrillDown({ title: `Método ${methodName}`, rows });
                    }}>
                      {methodRevenue.map((_, idx) => (
                        <Cell key={`m-${idx}`} fill={COLORS[idx % COLORS.length]} style={{ cursor: "pointer" }} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Top Clientes por Uso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Último Acesso</TableHead>
                      <TableHead className="text-right">Produtos</TableHead>
                      <TableHead className="text-right">Clientes</TableHead>
                      <TableHead className="text-right">Fornecedores</TableHead>
                      <TableHead className="text-right">Cat. Produtos</TableHead>
                      <TableHead className="text-right">Cat. Despesas</TableHead>
                      <TableHead className="text-right">Vendas</TableHead>
                      <TableHead className="text-right">Despesas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topTenantsByUsage.map((r) => (
                      <TableRow key={r.tenant_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{r.name}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: r.tenant_id, name: r.name })}>
                              <FileText className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{r.ultimo_acesso ? format(r.ultimo_acesso, "dd/MM/yyyy HH:mm", { locale: ptBR }) : "-"}</TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.produtos}</span>
                            {(pct(r.produtos, r.produtos_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.produtos, r.produtos_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.produtos, r.produtos_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.produtos_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.clientes}</span>
                            {(pct(r.clientes, r.clientes_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.clientes, r.clientes_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.clientes, r.clientes_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.clientes_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.fornecedores}</span>
                            {(pct(r.fornecedores, r.fornecedores_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.fornecedores, r.fornecedores_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.fornecedores, r.fornecedores_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.fornecedores_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.categorias_produto}</span>
                            {(pct(r.categorias_produto, r.categorias_produto_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.categorias_produto, r.categorias_produto_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.categorias_produto, r.categorias_produto_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.categorias_produto_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.categorias_despesa}</span>
                            {(pct(r.categorias_despesa, r.categorias_despesa_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.categorias_despesa, r.categorias_despesa_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.categorias_despesa, r.categorias_despesa_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.categorias_despesa_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.vendas}</span>
                            {(pct(r.vendas, r.vendas_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.vendas, r.vendas_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.vendas, r.vendas_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.vendas_prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.despesas}</span>
                            {(pct(r.despesas, r.despesas_prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.despesas, r.despesas_prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.despesas, r.despesas_prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.despesas_prev})</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cadastros" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Totais de Cadastros</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 grid-cols-2 md:grid-cols-4">
              <CountCellByPeriod label="Clientes SaaS" table="tenants" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'tenants', label: 'Clientes SaaS' })} />
              <CountCellByPeriod label="Usuários" table="profiles" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'profiles', label: 'Usuários' })} />
              <CountCellByPeriod label="Produtos" table="products" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'products', label: 'Produtos' })} />
              <CountCellByPeriod label="Clientes" table="clients" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'clients', label: 'Clientes' })} />
              <CountCellByPeriod label="Fornecedores" table="suppliers" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'suppliers', label: 'Fornecedores' })} />
              <CountCellByPeriod label="Contas Bancárias" table="bank_accounts" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'bank_accounts', label: 'Contas Bancárias' })} />
              <CountCellByPeriod label="Categorias de Produtos" table="product_categories" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'product_categories', label: 'Categorias de Produtos' })} />
              <CountCellByPeriod label="Categorias de Despesas" table="expense_categories" from={cadFrom} to={cadTo} prevFrom={prevFrom} prevTo={prevTo} onClick={() => setCadMetric({ key: 'expense_categories', label: 'Categorias de Despesas' })} />
              <RichCountCell label="Vendas" curr={(transactions || []).filter((t: any) => t.tipo === 'entrada').length} prev={(transactionsPrev || []).filter((t: any) => t.tipo === 'entrada').length} onClick={() => setCadMetric({ key: 'vendas', label: 'Vendas' })} />
              <RichCountCell label="Despesas" curr={(transactions || []).filter((t: any) => t.tipo === 'saida').length} prev={(transactionsPrev || []).filter((t: any) => t.tipo === 'saida').length} onClick={() => setCadMetric({ key: 'despesas', label: 'Despesas' })} />
              <RichCountCell label="Transações" curr={(transactions || []).length} prev={(transactionsPrev || []).length} onClick={() => setCadMetric({ key: 'transacoes', label: 'Transações' })} />
              <RichCountCell label="Pagamentos" curr={(confirmedPays || []).length} prev={(confirmedPaysPrev || []).length} onClick={() => setCadMetric({ key: 'pagamentos', label: 'Pagamentos' })} />
            </CardContent>
          </Card>

          {cadMetric && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Detalhe: {cadMetric.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = computeCadMetricRows(cadMetric.key);
                        const totalCurr = rows.reduce((sum: number, x: any) => sum + Number(x.curr || 0), 0);
                        return rows.map((r: any) => {
                          const diff = Number(r.curr || 0) - Number(r.prev || 0);
                          const participation = totalCurr > 0 ? ((Number(r.curr || 0) / totalCurr) * 100) : 0;
                          return (
                      <TableRow key={`cad-${cadMetric.key}-${r.tenant_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{r.name}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: r.tenant_id, name: r.name })}>
                              <FileText className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            <span>{r.curr}</span>
                            {(pct(r.curr, r.prev) >= 0) ? (
                              <TrendingUp className="h-3 w-3 text-success" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-destructive" />
                            )}
                            <span className={(pct(r.curr, r.prev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.curr, r.prev)).toFixed(1)}%</span>
                            <span className="text-muted-foreground">({r.prev})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">{r.prev}</TableCell>
                        <TableCell className="text-right {diff>=0? 'text-success':'text-destructive'}">{diff}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{participation.toFixed(1)}%</TableCell>
                      </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="frequencia" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Clientes Inativos (14 dias)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Última Atividade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveTenants.map((t) => (
                      <TableRow key={t.id} className="bg-warning/10">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{t.name}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: t.id, name: t.name })}>
                              <FileText className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{t.last ? format(t.last, "dd/MM/yyyy HH:mm", { locale: ptBR }) : "Nunca"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assinaturas" className="space-y-4 mt-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Receita Aprovada</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">{formatCurrency(kpis.subsRevenue)}</div>
                <p className="text-xs text-muted-foreground">{kpis.subsCount} pagamentos</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Tipos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie data={Object.entries(groupBy(subscriptionTxs, (x: any) => x.payment_type)).map(([k, arr]) => ({ tipo: ptPaymentType(k), key: k, qtd: (arr as any[]).length }))} dataKey="qtd" nameKey="tipo" outerRadius={80} onClick={(entry: any) => setDrillDown({ title: `Tipo ${entry?.tipo}`, rows: subscriptionTxs.filter((s: any) => s.payment_type === entry?.key) })}>
              {Object.entries(groupBy(subscriptionTxs, (x: any) => x.payment_type)).map((_, idx) => (
                <Cell key={`t-${idx}`} fill={COLORS[idx % COLORS.length]} style={{ cursor: "pointer" }} />
              ))}
            </Pie>
          </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Status de Assinaturas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(groupBy(tenants, (t: any) => t.subscription_status || "trial")).map(([k, arr]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span>{ptSubscriptionStatus(k)}</span>
                    <Badge variant={k === "active" ? "default" : k === "suspended" ? "destructive" : "secondary"}>{(arr as any[]).length}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Últimas Assinaturas Pagas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead className="text-center">Tipo</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Pagamento</TableHead>
                      <TableHead className="text-center">Expira</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(subscriptionTxs.filter((s: any) => s.status === 'approved' && s.payment_date)
                      .sort((a: any, b: any) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime())
                      .slice(0, 10)).map((s: any) => {
                        const tenant = tenants.find((t: any) => t.id === s.tenant_id);
                        const expanded = drillDown && drillDown.title === `Assinatura ${s.id}`;
                        return (
                          <>
                            <TableRow key={`sub-${s.id}`} className="cursor-pointer" onClick={() => {
                              const rows = subscriptionTxs.filter((x: any) => x.tenant_id === s.tenant_id).sort((a: any, b: any) => new Date(b.payment_date || b.created_at || 0).getTime() - new Date(a.payment_date || a.created_at || 0).getTime());
                              setDrillDown({ title: `Assinatura ${s.id}`, rows });
                            }}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span>{tenant?.name || s.tenant_id}</span>
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: s.tenant_id, name: tenant?.name || s.tenant_id })}>
                                    <FileText className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(Number(s.amount || 0))}</TableCell>
                              <TableCell className="text-center">{ptPaymentType(s.payment_type)}</TableCell>
                              <TableCell className="text-center">{ptSubscriptionStatus(s.status)}</TableCell>
                              <TableCell className="text-center">{s.payment_date ? format(new Date(s.payment_date), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-'}</TableCell>
                              <TableCell className="text-center">{s.expiration_date ? format(new Date(s.expiration_date), 'dd/MM/yyyy', { locale: ptBR }) : '—'}</TableCell>
                            </TableRow>
                            {expanded && (
                              <TableRow>
                                <TableCell colSpan={6}>
                                  <div className="mt-2">
                                    <p className="text-sm font-medium mb-2">Transações desta assinatura (cliente)</p>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Pagamento</TableHead>
                                          <TableHead className="text-right">Valor</TableHead>
                                          <TableHead>Tipo</TableHead>
                                          <TableHead>Status</TableHead>
                                          <TableHead>Expira</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {drillDown?.rows.map((row: any, idx: number) => (
                                          <TableRow key={`row-${idx}`}>
                                            <TableCell>{row.payment_date ? format(new Date(row.payment_date), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-'}</TableCell>
                                            <TableCell className="text-right">{formatCurrency(Number(row.amount || 0))}</TableCell>
                                            <TableCell>{row.payment_type || 'regular'}</TableCell>
                                            <TableCell>{ptSubscriptionStatus(row.status)}</TableCell>
                                            <TableCell>{row.expiration_date ? format(new Date(row.expiration_date), 'dd/MM/yyyy', { locale: ptBR }) : '—'}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transacoes" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Vendas e Saídas (Qtd e Valores)</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={temporalSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dia" />
                  <YAxis yAxisId="left" tickFormatter={(v) => Math.round(Number(v || 0)).toString()} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} />
                  <Tooltip formatter={(value: any, name: any, props: any) => {
                    const dk = props?.dataKey;
                    if (dk === 'receita' || dk === 'saidas') {
                      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
                    }
                    return Math.round(Number(value || 0));
                  }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="entradas_qtd" fill="#0ea5e9" name="Vendas (Qtd)" onClick={(d: any) => setDrillDown({ title: `Vendas (Qtd) do dia ${d?.dia || ''}`, rows: transactions.filter((t: any) => format(new Date(t.created_at), 'dd/MM', { locale: ptBR }) === d?.dia && t.tipo === 'entrada') })} cursor="pointer" />
                  <Bar yAxisId="right" dataKey="receita" fill="#16a34a" name="Vendas (Valores)" onClick={(d: any) => setDrillDown({ title: `Vendas (Valores) do dia ${d?.dia || ''}`, rows: transactions.filter((t: any) => format(new Date(t.created_at), 'dd/MM', { locale: ptBR }) === d?.dia && t.tipo === 'entrada') })} cursor="pointer" />
                  <Bar yAxisId="left" dataKey="saidas_qtd" fill="#f59e0b" name="Saídas (Qtd)" onClick={(d: any) => setDrillDown({ title: `Saídas (Qtd) do dia ${d?.dia || ''}`, rows: transactions.filter((t: any) => format(new Date(t.created_at), 'dd/MM', { locale: ptBR }) === d?.dia && t.tipo === 'saida') })} cursor="pointer" />
                  <Bar yAxisId="right" dataKey="saidas" fill="#ef4444" name="Saídas (Valores)" onClick={(d: any) => setDrillDown({ title: `Saídas (Valores) do dia ${d?.dia || ''}`, rows: transactions.filter((t: any) => format(new Date(t.created_at), 'dd/MM', { locale: ptBR }) === d?.dia && t.tipo === 'saida') })} cursor="pointer" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Últimas Transações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Líquido</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(transactions || [])
                      .slice()
                      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                      .slice(0, 20)
                      .map((t: any) => {
                        const tenant = tenants.find((x: any) => x.id === t.tenant_id);
                        const methodName = t.payment_method_id ? (pmName.get(String(t.payment_method_id)) || t.payment_method_id) : '-';
                        return (
                          <TableRow key={`tx-${t.id}`}>
                            <TableCell>{format(new Date(t.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span>{tenant?.name || t.tenant_id}</span>
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: t.tenant_id, name: tenant?.name || t.tenant_id })}>
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">{t.tipo}</TableCell>
                            <TableCell>{methodName}</TableCell>
                            <TableCell className="text-right">{formatCurrency(Number(t.valor_total || 0))}</TableCell>
                            <TableCell className="text-right">{formatCurrency(Number(t.valor_liquido || 0))}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ia" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Uso de IA por Cliente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Créditos usados</TableHead>
                      <TableHead className="text-right">% consumida</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiUsage.map((u: any, idx: number) => {
                      const t = tenants.find((x: any) => x.id === u.tenant_id);
                      const used = Number(u.used_credits || 0);
                      const total = Number(u.total_credits || 0);
                      const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                      return (
                        <TableRow key={`${u.tenant_id}-${idx}`} className={pct >= 80 ? "bg-warning/10" : ""}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{t?.name || u.tenant_id}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: u.tenant_id, name: t?.name || u.tenant_id })}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{used}</TableCell>
                          <TableCell className="text-right"><Badge variant={pct >= 80 ? "destructive" : "secondary"}>{pct}%</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engajamento" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base md:text-xl">Engajamento por Cliente (média diária)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Vendas/dia</TableHead>
                      <TableHead className="text-right">Saídas/dia</TableHead>
                      <TableHead className="text-right">Clientes/dia</TableHead>
                      <TableHead className="text-right">Fornecedores/dia</TableHead>
                      <TableHead className="text-right">Produtos/dia</TableHead>
                      <TableHead className="text-right">Total/dia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const days = Math.max(1, differenceInCalendarDays(cadTo, cadFrom) + 1);
                      const daysPrev = Math.max(1, differenceInCalendarDays(prevTo, prevFrom) + 1);
                      const vendasMap = new Map<string, number>();
                      const saidasMap = new Map<string, number>();
                      (transactions || []).forEach((t: any) => {
                        if (t.tipo === 'entrada') vendasMap.set(t.tenant_id, (vendasMap.get(t.tenant_id) || 0) + 1);
                        if (t.tipo === 'saida') saidasMap.set(t.tenant_id, (saidasMap.get(t.tenant_id) || 0) + 1);
                      });
                      const vendasPrevMap = new Map<string, number>();
                      const saidasPrevMap = new Map<string, number>();
                      (transactionsPrev || []).forEach((t: any) => {
                        if (t.tipo === 'entrada') vendasPrevMap.set(t.tenant_id, (vendasPrevMap.get(t.tenant_id) || 0) + 1);
                        if (t.tipo === 'saida') saidasPrevMap.set(t.tenant_id, (saidasPrevMap.get(t.tenant_id) || 0) + 1);
                      });
                      const prodMap = new Map<string, number>();
                      (allProducts || []).forEach((p: any) => prodMap.set(p.tenant_id, (prodMap.get(p.tenant_id) || 0) + 1));
                      const prodPrevMap = new Map<string, number>();
                      (allProductsPrev || []).forEach((p: any) => prodPrevMap.set(p.tenant_id, (prodPrevMap.get(p.tenant_id) || 0) + 1));
                      const cliMap = new Map<string, number>();
                      (allClients || []).forEach((c: any) => cliMap.set(c.tenant_id, (cliMap.get(c.tenant_id) || 0) + 1));
                      const cliPrevMap = new Map<string, number>();
                      (allClientsPrev || []).forEach((c: any) => cliPrevMap.set(c.tenant_id, (cliPrevMap.get(c.tenant_id) || 0) + 1));
                      const supMap = new Map<string, number>();
                      (allSuppliers || []).forEach((s: any) => supMap.set(s.tenant_id, (supMap.get(s.tenant_id) || 0) + 1));
                      const supPrevMap = new Map<string, number>();
                      (allSuppliersPrev || []).forEach((s: any) => supPrevMap.set(s.tenant_id, (supPrevMap.get(s.tenant_id) || 0) + 1));

                      const rowsById = (tenants || []).map((t: any) => {
                        const vendas = vendasMap.get(t.id) || 0;
                        const saidas = saidasMap.get(t.id) || 0;
                        const clientes = cliMap.get(t.id) || 0;
                        const fornecedores = supMap.get(t.id) || 0;
                        const produtos = prodMap.get(t.id) || 0;
                        const vendasPrev = vendasPrevMap.get(t.id) || 0;
                        const saidasPrev = saidasPrevMap.get(t.id) || 0;
                        const clientesPrev = cliPrevMap.get(t.id) || 0;
                        const fornecedoresPrev = supPrevMap.get(t.id) || 0;
                        const produtosPrev = prodPrevMap.get(t.id) || 0;
                        const vendasDia = vendas / days;
                        const saidasDia = saidas / days;
                        const clientesDia = clientes / days;
                        const fornecedoresDia = fornecedores / days;
                        const produtosDia = produtos / days;
                        const vendasDiaPrev = vendasPrev / daysPrev;
                        const saidasDiaPrev = saidasPrev / daysPrev;
                        const clientesDiaPrev = clientesPrev / daysPrev;
                        const fornecedoresDiaPrev = fornecedoresPrev / daysPrev;
                        const produtosDiaPrev = produtosPrev / daysPrev;
                        const totalDia = vendasDia + saidasDia + clientesDia + fornecedoresDia + produtosDia;
                        const totalDiaPrev = vendasDiaPrev + saidasDiaPrev + clientesDiaPrev + fornecedoresDiaPrev + produtosDiaPrev;
                        return {
                          id: t.id,
                          name: t.name,
                          vendasDia,
                          vendasDiaPrev,
                          saidasDia,
                          saidasDiaPrev,
                          clientesDia,
                          clientesDiaPrev,
                          fornecedoresDia,
                          fornecedoresDiaPrev,
                          produtosDia,
                          produtosDiaPrev,
                          totalDia,
                          totalDiaPrev,
                        };
                      });
                      const byName = new Map<string, any>();
                      rowsById.forEach((r: any) => {
                        const key = String(r.name || r.id).trim().toLowerCase();
                        const prev = byName.get(key);
                        if (!prev) {
                          byName.set(key, { ...r });
                        } else {
                          byName.set(key, {
                            id: prev.id,
                            name: prev.name,
                            vendasDia: (prev.vendasDia || 0) + (r.vendasDia || 0),
                            vendasDiaPrev: (prev.vendasDiaPrev || 0) + (r.vendasDiaPrev || 0),
                            saidasDia: (prev.saidasDia || 0) + (r.saidasDia || 0),
                            saidasDiaPrev: (prev.saidasDiaPrev || 0) + (r.saidasDiaPrev || 0),
                            clientesDia: (prev.clientesDia || 0) + (r.clientesDia || 0),
                            clientesDiaPrev: (prev.clientesDiaPrev || 0) + (r.clientesDiaPrev || 0),
                            fornecedoresDia: (prev.fornecedoresDia || 0) + (r.fornecedoresDia || 0),
                            fornecedoresDiaPrev: (prev.fornecedoresDiaPrev || 0) + (r.fornecedoresDiaPrev || 0),
                            produtosDia: (prev.produtosDia || 0) + (r.produtosDia || 0),
                            produtosDiaPrev: (prev.produtosDiaPrev || 0) + (r.produtosDiaPrev || 0),
                            totalDia: (prev.totalDia || 0) + (r.totalDia || 0),
                            totalDiaPrev: (prev.totalDiaPrev || 0) + (r.totalDiaPrev || 0),
                          });
                        }
                      });
                      const rows = Array.from(byName.values()).sort((a, b) => b.totalDia - a.totalDia);

                      if (rows.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground">Sem dados no período</TableCell>
                          </TableRow>
                        );
                      }

                      return rows.map((r, idx) => (
                        <TableRow key={`eng2-${idx}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{r.name}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => {
                                const tenant = tenants.find((t: any) => t.name === r.name);
                                setTenantProfile({ id: tenant?.id || "", name: r.name });
                              }}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.vendasDia.toFixed(2)}</span>
                              {(pct(r.vendasDia, r.vendasDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.vendasDia, r.vendasDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.vendasDia, r.vendasDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.vendasDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.saidasDia.toFixed(2)}</span>
                              {(pct(r.saidasDia, r.saidasDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.saidasDia, r.saidasDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.saidasDia, r.saidasDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.saidasDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.clientesDia.toFixed(2)}</span>
                              {(pct(r.clientesDia, r.clientesDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.clientesDia, r.clientesDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.clientesDia, r.clientesDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.clientesDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.fornecedoresDia.toFixed(2)}</span>
                              {(pct(r.fornecedoresDia, r.fornecedoresDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.fornecedoresDia, r.fornecedoresDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.fornecedoresDia, r.fornecedoresDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.fornecedoresDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.produtosDia.toFixed(2)}</span>
                              {(pct(r.produtosDia, r.produtosDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.produtosDia, r.produtosDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.produtosDia, r.produtosDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.produtosDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            <div className="inline-flex items-center gap-1">
                              <span>{r.totalDia.toFixed(2)}</span>
                              {(pct(r.totalDia, r.totalDiaPrev) >= 0) ? (
                                <TrendingUp className="h-3 w-3 text-success" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-destructive" />
                              )}
                              <span className={(pct(r.totalDia, r.totalDiaPrev) >= 0) ? "text-success" : "text-destructive"}>{Math.abs(pct(r.totalDia, r.totalDiaPrev)).toFixed(1)}%</span>
                              <span className="text-muted-foreground">({r.totalDiaPrev.toFixed(2)})</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="churn" className="space-y-4 mt-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Risco de Churn</CardTitle>
                <p className="text-sm text-muted-foreground">Definição e critérios</p>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div>
                  <p className="text-muted-foreground">O score de churn (0–100) estima a probabilidade de cancelamento com base em:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
                    <li>Status da assinatura Cancelada/Suspensa (+50).</li>
                    <li>Próxima cobrança ausente ou vencida (+10).</li>
                    <li>Último pagamento há &gt; 30 dias (+30).</li>
                    <li>Inatividade no período (sem transações) (+20).</li>
                    <li>Desvio de frequências esperadas por tipo de cadastro (usuários, produtos, clientes, fornecedores, categorias, vendas, despesas, pagamentos, contas, sessões), ponderado por peso do tipo.</li>
                    <li>Tendência vs média histórica desde a criação do tenant (queda acentuada aumenta risco; recuperação reduz risco).</li>
                  </ul>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">Interpretação</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
                    <li>Risco alto: ≥ 50</li>
                    <li>Risco médio: 30–49</li>
                    <li>Risco baixo: &lt; 30</li>
                  </ul>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground">Atualização e persistência</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
                    <li>Os cálculos consideram o período ativo da tela.</li>
                    <li>Resultados são persistidos em <code>tenant_health</code> com última atualização registrada.</li>
                    <li>Recalcula automaticamente quando o último cálculo estiver defasado (tolerância configurável).</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-xl">Lista por Risco</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {churnRisk.map((r) => (
                        <TableRow key={`churn-${r.id}`} className={r.score >= 50 ? "bg-destructive/10" : r.score >= 30 ? "bg-warning/10" : ""}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{r.name}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setTenantProfile({ id: r.id, name: r.name })}>
                                <FileText className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold">{r.score}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!drillDown} onOpenChange={() => setDrillDown(null)}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{drillDown?.title || "Detalhes"}</DialogTitle>
            <DialogDescription>{drillDown?.rows.length || 0} itens</DialogDescription>
          </DialogHeader>
          {drillDown && drillDown.rows.length > 0 ? (
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDown.rows.map((row: any, idx: number) => (
                    <TableRow key={`dr-${idx}`}>
                      <TableCell>{format(new Date(row.created_at || row.payment_date || new Date()), "dd/MM/yyyy HH:mm", { locale: ptBR })}</TableCell>
                      <TableCell>{(() => { const t = tenants.find((x: any) => x.id === (row.tenant_id || row.transactions?.tenant_id)); return t?.name || row.tenant_id || "-"; })()}</TableCell>
                      <TableCell className="capitalize">{row.tipo || row.status || "-"}</TableCell>
                      <TableCell className="text-right">{formatCurrency(Number(row.valor_liquido || row.amount || row.valor || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">Nenhum item encontrado</div>
          )}
        </DialogContent>
      </Dialog>

      {(typeof tenantProfile !== "undefined") && tenantProfile && (
        <RaioXModal
          open={!!tenantProfile}
          onOpenChange={(open) => { if (!open) setTenantProfile(null); }}
          entityTypeLabel={"CLIENTE SAAS"}
          entityName={tenantProfile.name}
          infoItems={[]}
          showPeriodControls
          initialFrom={cadFrom}
          initialTo={cadTo}
          contentClassName="p-4"
          renderContent={({ periodRange, setLoading }) => (
            <TenantDetails id={tenantProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
          )}
        />
      )}
    </div>
  );
};

const CountCell = ({ label, table }: { label: string; table: string }) => {
  const { isAdminSaas } = useTenant();
  const { data } = useQuery({
    queryKey: ["count-" + table],
    queryFn: async () => {
      const { count, error } = await supabase.from(table as any).select("id", { count: "exact", head: true });
      if (error) throw error;
      return count || 0;
    },
    enabled: isAdminSaas,
  });
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-bold text-2xl">{data || 0}</p>
    </div>
  );
};

const CountCellByPeriod = ({ label, table, from, to, prevFrom, prevTo, onClick }: { label: string; table: string; from: Date; to: Date; prevFrom: Date; prevTo: Date; onClick?: () => void }) => {
  const { isAdminSaas } = useTenant();
  const { data: curr = 0 } = useQuery({
    queryKey: ["count-period-" + table, from.toISOString(), to.toISOString()],
    queryFn: async () => {
      const { count, error } = await supabase
        .from(table as any)
        .select("id", { count: "exact", head: true })
        .gte("created_at", from.toISOString())
        .lte("created_at", to.toISOString());
      if (error) throw error;
      return count || 0;
    },
    enabled: isAdminSaas,
  });
  const { data: prev = 0 } = useQuery({
    queryKey: ["count-period-prev-" + table, prevFrom.toISOString(), prevTo.toISOString()],
    queryFn: async () => {
      const { count, error } = await supabase
        .from(table as any)
        .select("id", { count: "exact", head: true })
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString());
      if (error) throw error;
      return count || 0;
    },
    enabled: isAdminSaas,
  });
  const delta = (() => {
    if (!prev) return curr > 0 ? 100 : 0;
    return ((Number(curr) - Number(prev)) / Number(prev)) * 100;
  })();
  const up = delta >= 0;
  return (
    <div className={onClick ? "cursor-pointer" : undefined} onClick={onClick}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <p className="font-bold text-2xl">{curr || 0}</p>
        <div className="inline-flex items-center gap-1 text-xs">
          {up ? (
            <TrendingUp className="h-3 w-3 text-success" />
          ) : (
            <TrendingDown className="h-3 w-3 text-destructive" />
          )}
          <span className={up ? "text-success" : "text-destructive"}>{Math.abs(delta).toFixed(1)}%</span>
          <span className="text-muted-foreground">({prev || 0})</span>
        </div>
      </div>
    </div>
  );
};

function groupBy<T>(arr: T[], fn: (x: T) => string) {
  const map = new Map<string, T[]>();
  arr.forEach((x) => { const k = fn(x); map.set(k, [...(map.get(k) || []), x]); });
  return Object.fromEntries(Array.from(map.entries()));
}

function ptSubscriptionStatus(k: string) {
  if (k === "active") return "Ativa";
  if (k === "trial") return "Avaliação";
  if (k === "suspended") return "Suspensa";
  if (k === "cancelled") return "Cancelada";
  return k;
}

function ptPaymentType(k: string | null | undefined) {
  const v = (k || "regular").toLowerCase();
  if (v === "regular") return "Regular";
  if (v === "cortesia") return "Cortesia";
  if (v === "testers") return "Testers";
  if (v === "promotion") return "Promoção";
  return v;
}

function RichCountCell({ label, curr, prev, onClick }: { label: string; curr: number; prev: number; onClick?: () => void }) {
  const delta = (() => {
    if (!prev) return curr > 0 ? 100 : 0;
    return ((Number(curr) - Number(prev)) / Number(prev)) * 100;
  })();
  const up = delta >= 0;
  return (
    <div className={onClick ? "cursor-pointer" : undefined} onClick={onClick}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <p className="font-bold text-2xl">{curr || 0}</p>
        <div className="inline-flex items-center gap-1 text-xs">
          {up ? (
            <TrendingUp className="h-3 w-3 text-success" />
          ) : (
            <TrendingDown className="h-3 w-3 text-destructive" />
          )}
          <span className={up ? "text-success" : "text-destructive"}>{Math.abs(delta).toFixed(1)}%</span>
          <span className="text-muted-foreground">({prev || 0})</span>
        </div>
      </div>
    </div>
  );
}

function computeCadMetricRowsFactory(params: {
  tenants: any[];
  maps: Record<string, { curr: Map<string, number>; prev: Map<string, number> }>;
}) {
  return (key: string) => {
    const m = params.maps[key];
    if (!m) return [] as any[];
    const byName = new Map<string, { tenant_id: string; name: string; curr: number; prev: number }>();
    (params.tenants || []).forEach((t: any) => {
      const curr = m.curr.get(t.id) || 0;
      const prev = m.prev.get(t.id) || 0;
      const k = String(t.name || t.id).trim().toLowerCase();
      const prevRow = byName.get(k);
      if (!prevRow) {
        byName.set(k, { tenant_id: t.id, name: t.name, curr, prev });
      } else {
        byName.set(k, { tenant_id: prevRow.tenant_id, name: prevRow.name, curr: (prevRow.curr || 0) + curr, prev: (prevRow.prev || 0) + prev });
      }
    });
    const rows = Array.from(byName.values());
    return rows.sort((a, b) => (b.curr - b.prev) - (a.curr - a.prev));
  };
}

export default SaasOverview;
